/**
 * Admin support panel (Chat de dúvidas) API client.
 * Wraps apiGet/apiPost/apiFetch for /api/admin/help endpoints.
 */

import { apiFetch, apiGet, apiPost } from '@/services/apiClient';

// ============================================================================
// TYPES
// ============================================================================

export type HelpPeriod = '7d' | '30d' | '90d';

export type HelpStatusFilter = '' | 'unknown' | 'needs_team' | 'thumbs_down';

export type HelpFilters = { plan: string; status: HelpStatusFilter; q: string };

export interface HelpSummary {
  period: HelpPeriod;
  conversations: number;
  questions: number;
  people: number;
  unknown: number;
  needs_team: number;
  thumbs_up: number;
  thumbs_down: number;
  model_errors: number;
  cost_usd: number;
  topics: { topic: string; count: number }[];
  screens: { screen: string; count: number }[];
}

export interface HelpListRow {
  id: string;
  title: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  plan: string | null;
  questions: number;
  screenshots: number;
  unknown: boolean;
  needs_team: boolean;
  thumbs_down: boolean;
  updated_at: string;
}

export interface HelpListPage {
  total: number;
  page: number;
  size: number;
  rows: HelpListRow[];
}

export interface HelpAdminMessage {
  id: string;
  role: 'user' | 'assistant' | 'team';
  text: string;
  image_url: string | null;
  created_at: string;
  feedback: 1 | -1 | null;
  screen: string | null;
  knew: boolean | null;
  topic: string | null;
  needs_team: boolean;
  plan: string | null;
  model: string | null;
  cost_usd: number | null;
}

export interface HelpAdminThread {
  conversation: { id: string; title: string; created_at: string; updated_at: string };
  user: { email: string | null; display_name: string | null } | null;
  messages: HelpAdminMessage[];
}

export interface HelpNote {
  id: string;
  kind: 'faq' | 'notice';
  question: string | null;
  answer: string;
  active: boolean;
  created_by: string | null;
  created_at: string;
  expires_at: string | null;
}

export type HelpNoteInput = {
  kind: 'faq' | 'notice';
  question: string | null;
  answer: string;
  expiresAt: string | null;
};

export type HelpNotePatch = {
  active?: boolean;
  answer?: string;
  question?: string | null;
  expiresAt?: string | null;
};

export interface HelpSettings {
  enabled: boolean;
  audience: 'admins' | 'all';
  model: string;
  fallbackModel: string;
  dailyCap: number;
  monthlyCapUsd: number;
}

// ============================================================================
// HELPERS
// ============================================================================

const BASE = '/api/admin/help';

/** Query string for summary/list: empty filters are left out. */
function filterParams(period: HelpPeriod, filters: HelpFilters): Record<string, string> {
  const params: Record<string, string> = { period };
  const plan = filters.plan.trim();
  const q = filters.q.trim();
  if (plan) params.plan = plan;
  if (filters.status) params.status = filters.status;
  if (q) params.q = q;
  return params;
}

/** The API's `{ error }` text (apiFetch throws it as Error.message), for inline display. */
export const helpErrorMessage = (err: unknown) =>
  err instanceof Error && err.message ? err.message : 'Algo deu errado. Tente de novo.';

// ============================================================================
// SUMMARY / CONVERSATIONS
// ============================================================================

export const getHelpSummary = (period: HelpPeriod, filters: HelpFilters) =>
  apiGet<HelpSummary>(`${BASE}/summary`, filterParams(period, filters));

export const getHelpConversations = (period: HelpPeriod, filters: HelpFilters, page: number) =>
  apiGet<HelpListPage>(`${BASE}/conversations`, { ...filterParams(period, filters), page: String(page) });

export const getHelpConversation = (id: string) => apiGet<HelpAdminThread>(`${BASE}/conversations/${id}`);

type HelpReplyMessage = Pick<HelpAdminMessage, 'id' | 'role' | 'text' | 'created_at'> & Partial<HelpAdminMessage>;

// The reply route returns only the base message fields; the admin-only meta fields get their empty values.
export const replyHelpConversation = async (id: string, text: string): Promise<{ message: HelpAdminMessage }> => {
  const { message } = await apiPost<{ message: HelpReplyMessage }>(`${BASE}/conversations/${id}/reply`, { text });
  return {
    message: {
      image_url: null,
      feedback: null,
      screen: null,
      knew: null,
      topic: null,
      needs_team: false,
      plan: null,
      model: null,
      cost_usd: null,
      ...message,
    },
  };
};

// ============================================================================
// NOTES (FAQ / AVISOS)
// ============================================================================

export const listHelpNotes = () => apiGet<{ notes: HelpNote[] }>(`${BASE}/notes`);

export const createHelpNote = (body: HelpNoteInput) => apiPost<{ note: HelpNote }>(`${BASE}/notes`, body);

export const updateHelpNote = (id: string, patch: HelpNotePatch) =>
  apiFetch<{ note: HelpNote }>(`${BASE}/notes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

// ============================================================================
// SETTINGS
// ============================================================================

export const getHelpSettings = () => apiGet<HelpSettings>(`${BASE}/settings`);

export const patchHelpSettings = (patch: Partial<HelpSettings>) =>
  apiFetch<HelpSettings>(`${BASE}/settings`, { method: 'PATCH', body: JSON.stringify(patch) });
